// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/useAskV2PlanProgress.test.ts
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  ASK_V2_PLAN_RUNNING_ID,
  ASK_V2_PLAN_SKIPPED_DETAIL,
  ASK_V2_PLAN_STARTING_ID,
  ASK_V2_PLAN_WRITING_ID,
  buildAskV2PlanProgress,
  inFlightAgentStep,
  planScopeKey,
  readTurnPlanSteps,
} from "./buildRailProgressFromPlan";
import { checkRowLabel } from "./checkNames";
import {
  checkProgressFromMessages,
  nestCheckProgress,
} from "./checkProgressRows";
import {
  type AskV2ReviewProgressMode,
  messagesAfterLatestReviewStart,
  messagesOfLastTurn,
  messagesOfRunningTurn,
} from "./collectRailContent";
import { type AskV2RailProgressItem } from "./types";

/**
 * The scope a turn that is not in the thread yet reads.
 *
 * One module-level array, not a fresh `[]` per render: it is a `useMemo`
 * dependency, and a new identity every render would rebuild the plan rows on
 * every frame the socket delivers.
 */
const EMPTY_MESSAGES: readonly AskPunttMessage[] = [];

/**
 * The review rail's rows, read off the checklist warhol writes with
 * `write_plan`.
 *
 * The rows used to be a client-side timer script on a three-minute budget, so
 * they advanced whether or not the review did. They now report only what the
 * model put in its own plan, which is why a step can sit `pending` for a long
 * time: that is the review's real position.
 *
 * The rows are derived from persisted messages, so a reload or a second tab
 * rebuilds the same rail instead of restarting it -- the old script kept its
 * clock in per-tab `sessionStorage` and began again from the first step.
 *
 * The plan's OWN steps are the top level, and the checks warhol settles during
 * the turn hang one level under the row the turn is working on. #5686 removed
 * that nesting; Roberto ordered it back on 2026-10-14, because "each check
 * ticks off on its own" is the story's clause and a 4-6 step summary cannot
 * show it: a step can sit `active` for twenty minutes while the forty checks
 * behind it settle one at a time, and the ticks are what say the review is
 * still moving.
 *
 * Restored, NOT re-derived: the rows are built by `checkProgressRows`, which is
 * the module #5686 deleted, with one deliberate change -- a `skipped` check
 * draws `blocked` with its reason instead of a struck-through `done`. The
 * thread's chip reads the same entry the same way (`rowTick`), and the two
 * surfaces describe one truth.
 */
export function useAskV2PlanProgress(input: {
  messages?: readonly AskPunttMessage[];
  mode: AskV2ReviewProgressMode;
  /**
   * Whether the transport says a turn is in flight right now.
   *
   * Pass `transport.inFlight`, NOT `transport.streamMessageId != null`. The
   * armed address is a SHAPE read: it is derived from the transcript, whose
   * placeholder stays `pending` until the settled answer is mirrored into it,
   * so it outlives its own turn by one to three polls -- and when the turn
   * settled with no answer row it never clears at all. `transport.inFlight`
   * consumes warhol's terminal frame and vetoes on it, so it is the same
   * ending this hook needs and it drops the moment the turn does (N5). A
   * caller that still passes the armed address is handled too, off the row the
   * frame is stamped on -- see `turnSettled` -- but only for a turn that left
   * a row behind.
   *
   * The rail's own evidence for "a turn is running" is a `pending` assistant
   * row in the thread, and that row is not always there while the turn is:
   * `useAskV2Transport` hands the rail `applyStreamSnapshot(base, snapshot)`
   * the moment the history payload names the running turn, and that path
   * SKIPS `mergeDisplayMessages` -- so the optimistic in-flight assistant row
   * the ACK appended is left behind. With no live fields on the frame yet
   * (`agentSteps` empty, no text, no checks) the overlay then appends nothing
   * either, and the newest assistant row the mode reads is the PREVIOUS turn's
   * answer. The mode fell to `idle` and the Progress section unmounted, then
   * remounted on the first frame that carried anything -- the blink Roberto
   * reported at the start of a turn.
   *
   * The transport already knows better and says so with `streamMessageId`: it
   * names the running turn, and it HOLDS through this window by construction --
   * it is built off the transport's own rows until the payload catches up
   * (`historyPayloadCoversLocalTurn` is the hysteresis), so a stale payload
   * that predates the send cannot un-name the turn the person just started.
   * That address is the single source of truth for "turn in flight" here.
   *
   * It raises `idle` to `running` while it is true, and it is absent on every
   * chat the server names as rembrandt, because nothing arms it. Its FALSE
   * value is not read here -- `turnSettled` is the ending, and it needs the
   * armed address as its scope so a follow-up is never closed by the turn
   * before it.
   */
  turnInFlight?: boolean;
  /**
   * The message the armed stream address belongs to (`transport.streamMessageId`
   * without its `w`).
   *
   * The address NAMES its turn, and the thread this hook reads either carries
   * that turn or does not. That is the whole distinction the mode needs and the
   * one `turnInFlight` cannot draw:
   *
   * - NOT in `messages` -- the turn has been accepted and armed but the thread
   *   has not caught up. A follow-up is sent in this window, and the mode still
   *   describes the review it follows (`completed`), so scoping to the review
   *   cycle puts that review's finished plan on the rail.
   * - in `messages` -- the address outlived its own turn by a frame or two,
   *   which is the case `turnInFlight`'s docblock already protects: a finished
   *   review is finished, and nothing here may raise it back to running.
   */
  turnInFlightUserId?: string | null;
  /**
   * Whether a send made in THIS tab is still waiting for its ACK
   * (`transport.sending`).
   *
   * The optimistic user row a send appends lives on the transport, not on
   * `historyMessages` -- the list this hook reads -- so between the send and the
   * ACK there is no address yet to name, and no row in the thread either. This
   * is the earlier half of the same window as `turnInFlightUserId`.
   *
   * It ends with the ACK: `sending` clears when the row is marked sent, and the
   * stream address takes over from there. A failed send clears it too and marks
   * the row `failed`, which is why this cannot hold a phantom turn open.
   */
  turnPending?: boolean;
}): {
  items: AskV2RailProgressItem[];
  showShell: boolean;
} {
  const { t } = useTranslation("ui");
  const { messages, mode } = input;

  const cycle = useMemo(
    () => messagesAfterLatestReviewStart(messages ?? []),
    [messages],
  );

  const turn = useMemo(() => {
    const all = messages ?? [];
    const running = messagesOfRunningTurn(all);
    if (!running?.[0]) return null;

    // A stale placeholder from an earlier review can survive in overlaid
    // history briefly. Never let that older turn cross the latest Start review
    // boundary and revive its plan over the new review.
    const latestStartIndex = all.length - cycle.length - 1;
    const turnStartIndex = all.indexOf(running[0]);
    return turnStartIndex >= latestStartIndex ? running : null;
  }, [cycle, messages]);
  const turnRunning = turn !== null;

  /**
   * A turn this tab has started that the thread does not carry yet.
   *
   * Two windows, and the mode still describes the previous turn in both:
   *
   * - `turnPending` -- the send was taken locally and no ACK has landed. The
   *   optimistic row lives on the transport, so `messages` has not changed at
   *   all yet, and there is not even an address to name the turn.
   * - an armed address naming a message `messages` does not carry -- the ACK
   *   landed, the socket is armed, and the history payload the thread is built
   *   from has not returned the follow-up yet (it is up to a poll away).
   *
   * A follow-up is sent in exactly these two, which is why scoping to the
   * review cycle there put the PREVIOUS review's finished plan on the rail for
   * the first tick of a turn that had not planned anything: `cycle` is
   * everything after the last "Start review", the new turn has not written into
   * it yet, so the newest plan in that slice is the old one.
   *
   * Deliberately NOT `turnInFlight` alone. An address can outlive its own turn
   * by a frame or two, and the turn it names is then IN the thread -- a
   * finished review whose plan must stay up, ticked. Requiring the address's
   * message to be absent is what separates "a new turn has started" from "the
   * last turn's address has not been torn down yet".
   */
  const turnInFlightUserId = input.turnInFlightUserId ?? null;
  const turnOutstanding =
    turn === null &&
    (input.turnPending === true ||
      (turnInFlightUserId != null &&
        !(messages ?? []).some(
          (message) => message?._id === turnInFlightUserId,
        )));

  /**
   * The turn that just ended, which a settled rail draws its plan from.
   *
   * The cycle is the wrong slice for that: it starts at the last "Start
   * review", which a follow-up never leaves, so the newest plan in it is the
   * PREVIOUS turn's whenever the follow-up did not plan. Scoping to the last
   * turn makes a plan-less follow-up show no plan once it settles, instead of
   * the plan of the turn before it. See `messagesOfLastTurn`.
   */
  const lastTurn = useMemo(
    () => messagesOfLastTurn(messages ?? []),
    [messages],
  );

  /**
   * Whether the LAST turn's own newest assistant row says the turn is over.
   *
   * The row is where the ending arrives. `applyStreamSnapshot` stamps warhol's
   * terminal frame (`snapshot.status`) onto the running turn's row as
   * `status: "completed"`, exactly as christo's persisted answer carries it —
   * so "the newest assistant row of the newest turn is not `pending`" is the
   * thread's own read of the same ending the composer now consumes.
   *
   * Scoped to `turn === null` at the call site, which is what separates this
   * from a live turn whose row has not been written yet: while a turn runs,
   * `messagesOfRunningTurn` names it and `turn` is non-null. A turn that ends
   * with no answer row at all leaves nothing here to read, which is why the
   * transport's own veto is consulted too.
   */
  const lastTurnAnswered = useMemo(() => {
    const rows = lastTurn ?? [];
    for (let index = rows.length - 1; index >= 0; index -= 1) {
      const message = rows[index];
      if (!message || message.hidden || message.role !== "assistant") continue;
      return message.status !== "pending";
    }
    return false;
  }, [lastTurn]);

  /**
   * Whether the turn the armed address names has ENDED.
   *
   * The rail's evidence for "a turn is running" is a `pending` assistant row,
   * and the frame that clears it was applied and never consumed: the mode this
   * hook is handed comes from `collectRailContent`, whose end-of-cycle reads
   * are all about the PERSISTED review — a finished review row, a `reviewIds`
   * array — and those land a poll (or, when the turn settled with no answer
   * row, never) after the turn has actually ended. So the rail kept an
   * `active` row and its "Writing the answer…" row up over a turn the server
   * had finished, and the last rows never ticked off (N5).
   *
   * Two reads of the ONE ending, because the two surfaces wire the signal
   * differently:
   *
   * * `turnInFlight === false` — the caller hands the transport's own honest
   *   `inFlight`, which `useAskV2Transport` vetoes on the terminal
   *   `snapshot.status`. This is the only read that covers a turn that settled
   *   with no answer row, because then there is no row to read.
   * * `lastTurnAnswered` — the caller still hands the armed address. The stamp
   *   is on the turn's row, so a thread whose newest turn no longer ends on a
   *   `pending` assistant row has ended too.
   *
   * Both are scoped by the address and by `turnOutstanding`, because a
   * follow-up this tab has just sent is NOT the previous turn's ending: the
   * rail has to go on drawing it as running.
   */
  const turnSettled =
    turnInFlightUserId != null &&
    !turnOutstanding &&
    (input.turnInFlight === false ||
      (input.turnInFlight === true && turn === null && lastTurnAnswered));

  // A turn in flight is running, whatever the thread's pending-row evidence
  // says this render. `completed` outranks it -- a finished review is finished
  // -- but the review a turn this tab just started will replace is not that
  // review, so this outranks `completed` too. Both are FLOORS on "is a turn
  // running now"; neither claims one is already done.
  //
  // And the ending outranks both, in the one window where the floors would
  // otherwise hold a finished review open: the address stays armed until the
  // mirror lands the settled answer, so `turnInFlight` alone kept the mode
  // `running` for as long as that took. `turnOutstanding` still outranks the
  // ending -- a follow-up is running whatever the turn before it did.
  const effectiveMode: AskV2ReviewProgressMode = turnOutstanding
    ? "running"
    : turnSettled
      ? "completed"
      : mode === "idle" && input.turnInFlight === true
        ? "running"
        : mode;

  // A live turn is narrower than a review cycle. This prevents a completed
  // review's plan from flashing over a follow-up that has not planned yet --
  // and with no turn in the thread yet, an outstanding one is scoped to
  // NOTHING rather than to the cycle the old plan is sitting in.
  //
  // With nothing running and nothing outstanding, the LAST turn answers, not
  // the cycle: a follow-up that never planned must not inherit the plan of the
  // turn before it once its own turn is over.
  const scoped = useMemo(
    () =>
      turn ?? (turnOutstanding ? EMPTY_MESSAGES : (lastTurn ?? EMPTY_MESSAGES)),
    [lastTurn, turn, turnOutstanding],
  );

  // Folded over every `write_plan` call in the slice, not read off the newest
  // one: the model re-emits its checklist with finished steps marked unfinished,
  // and a turn may not reopen a plan it has already shown finished.
  const steps = useMemo(() => readTurnPlanSteps(scoped), [scoped]);

  // A call still waiting for its result, so the rail can say "writing the
  // answer" only when that is what the turn is actually doing.
  const inFlight = useMemo(() => inFlightAgentStep(scoped), [scoped]);

  // What warhol has settled so far this turn, off the row christo keeps
  // rewriting. Scoped to the SAME slice the plan is, not to the review cycle:
  // the pending row of the PREVIOUS review keeps its checks until the answer
  // replaces the row in place, so a read across the cycle would tick the last
  // review's checks under this one's plan -- and the checks must never come
  // from a different turn than the steps they hang under. Empty for every turn
  // that is not warhol's, and for a warhol turn whose progress row could not be
  // read: in both cases the rail draws exactly the plan rows it drew before
  // this existed.
  const checkProgress = useMemo(
    () => checkProgressFromMessages(scoped),
    [scoped],
  );

  // The plan's own address, so two plans in one thread cannot share row ids.
  // Off the TURN, and only while one is running; see `planScopeKey`.
  const planKey = useMemo(() => planScopeKey(turn), [turn]);

  const items = useMemo(() => {
    if (effectiveMode === "idle") return [];
    const planned = buildAskV2PlanProgress({
      steps,
      // Review-cycle status can become completed while its active turn still
      // runs. Preserve the plan's own statuses until that turn settles -- and
      // `turnSettled` IS that settle, said by the server rather than waited for
      // in the persisted review, so it ticks the rows the moment the frame
      // lands instead of a poll later (N5).
      completed: effectiveMode === "completed" && (!turnRunning || turnSettled),
      ...(planKey ? { planKey } : {}),
    });
    if (planned.length > 0) {
      // The builder is pure and i18n-free, so the skipped row arrives marked.
      // Translate it here, where `t` lives, or the rail renders untranslated
      // English in every locale but English.
      const rows = planned.map((item) =>
        item.detail === ASK_V2_PLAN_SKIPPED_DETAIL
          ? { ...item, detail: t("askV2.rail.progressSteps.skipped") }
          : item,
      );
      /**
       * Every step ticked while the turn still runs.
       *
       * The plan's own steps are genuinely all `completed` -- the model
       * finished its checklist -- and the turn is now writing the prose answer,
       * which is the longest single stretch of a review. Without a row for it
       * the rail is an all-ticked list next to a live Stop button for the
       * 15-20s that takes, which says the turn is over when it is not.
       *
       * Only while a turn is running, and only when nothing else is still
       * open: with a step `pending` or `active` the rows already say where the
       * turn is, and a second active row would claim two things at once. It
       * drops off at settle because `stillRunning` is false there -- which is
       * also when `completed` ticks the last rows, so the list ends all-done
       * exactly when the turn does.
       */
      // Not while the server has said the turn is over: a stale `pending`
      // placeholder (or the person's own last message, when the turn settled
      // with no answer row) would otherwise hold "Writing the answer…" up over
      // a finished turn for good.
      const stillRunning =
        !turnSettled && (effectiveMode === "running" || turnRunning);
      const allTicked = rows.every((item) => item.status === "done");
      if (!stillRunning || !allTicked) return rows;
      /**
       * All the plan's steps are ticked and the turn is not over, so the rail
       * owes the person one more row saying what it is waiting for -- an
       * all-ticked rail under a live Stop button reads as a turn that has
       * finished. WHICH row depends on what the live steps say the model is
       * doing right now.
       *
       * - a call with no result yet is work in flight. Naming it is honest, and
       *   claiming the answer is being written over an open call is not: the
       *   gate-2 A-chat turn showed "Writing the answer…" at t=112-113 while a
       *   `write_plan` call was still open.
       * - the newest step being a result means nothing is outstanding, and the
       *   prose answer is the one thing left.
       *
       * `displayName` is required on a `tool_call` and is the same label the
       * thread's own row for that call carries, so the rail and the thread name
       * the call the same way -- and a call that cannot be named adds no row
       * rather than a claim the rail cannot support.
       */
      if (inFlight) {
        const label = inFlight.displayName || inFlight.statusName;
        return label
          ? [
              ...rows,
              {
                id: ASK_V2_PLAN_RUNNING_ID,
                label,
                status: "active" as const,
              },
            ]
          : rows;
      }
      return [
        ...rows,
        {
          id: ASK_V2_PLAN_WRITING_ID,
          label: t("askV2.rail.progressSteps.writingAnswer"),
          status: "active" as const,
        },
      ];
    }
    // `turnSettled` closes this too: a settled turn that wrote no plan has
    // nothing to say, and a "Getting started" SPINNER over a finished turn is
    // the same defect as the un-ticked last row.
    if (effectiveMode !== "running" && (!turnRunning || turnSettled)) return [];
    // No plan yet. A turn that needs one tool call never writes one, and a
    // planned turn is still deciding, so this is the only row that may be
    // shown without the backend having reported it -- and it claims nothing
    // but that the turn has started.
    return [
      {
        id: ASK_V2_PLAN_STARTING_ID,
        label: t("askV2.rail.progressSteps.starting"),
        status: "active" as const,
      },
    ];
  }, [effectiveMode, inFlight, planKey, steps, t, turnRunning, turnSettled]);

  // The checks ride the row the turn is working on. The tally's copy is built
  // here, where `t` lives, for the same reason the skipped row's is: the
  // nesting module is pure and i18n-free. So is the check's own LABEL -- warhol
  // names its checks (`barcode_zone`, `identifier-reader`) and `checkNames`
  // turns one into a person's words, which needs `t` too.
  const rows = useMemo(
    () =>
      nestCheckProgress(
        items,
        checkProgress,
        (tally) =>
          t("askV2.rail.progressSteps.verified", {
            verified: tally.verified,
            total: tally.total,
          }),
        (name) => checkRowLabel(name, t),
      ),
    [items, checkProgress, t],
  );

  return { items: rows, showShell: effectiveMode !== "idle" };
}
